import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  postConcurrentSearchRequest,
  setConcurrentSearchLoading,
  resetConcurrentSearch,
} from '@client/model/concurrentSearchModel';
import { AppState } from '@client/model/store';

interface UseConcurrentSearchParams {
  requesterId?: string;
  aiPromptType?: string;
}

interface UseConcurrentSearchCallbacks {
  onSuccess?: (data: OpenAiCompletionDbRecord) => void;
  onError?: () => void;
}

interface UseConcurrentSearchResult {
  isSearching: boolean;
  startSearch: (
    params: UseConcurrentSearchParams,
    callbacks?: UseConcurrentSearchCallbacks,
  ) => void;
  clearCache: () => void;
}

export function useConcurrentSearch(searchKey: string): UseConcurrentSearchResult {
  const dispatch = useDispatch();
  const searchState = useSelector((state: AppState) => state.concurrentSearch.searches[searchKey]);
  const [searchCallbacks, setSearchCallbacks] = useState<UseConcurrentSearchCallbacks | null>(null);

  const startSearch = (
    params: UseConcurrentSearchParams,
    callbacks?: UseConcurrentSearchCallbacks,
  ) => {
    if (!params.requesterId || !params.aiPromptType) {
      return;
    }

    //Commented this out as a hotfix as this is cached per carrier and serving the same stale data for all subsequent client searches
    // // Check if we already have cached data for this search
    // if (searchState?.data && !!searchState.data.results) {
    //   console.log(`[useConcurrentSearch] Cache HIT for searchKey: ${searchKey}`);
    //   // Return cached data immediately
    //   callbacks?.onSuccess?.(searchState.data);
    //   return;
    // }

    //console.log(`[useConcurrentSearch] Cache MISS for searchKey: ${searchKey} - making API call`);

    // No cached data, proceed with search
    dispatch(resetConcurrentSearch(searchKey));
    dispatch(setConcurrentSearchLoading({ searchKey, isLoading: true }));
    setSearchCallbacks(callbacks || null);

    dispatch(
      postConcurrentSearchRequest({
        ...params,
        searchKey,
      }),
    );
  };

  useEffect(() => {
    if (!searchState || searchState.isLoading || !searchCallbacks) return;

    // Check for error first
    if (searchState.error) {
      searchCallbacks.onError?.();
      setSearchCallbacks(null);
      return;
    }

    // Search found existing data
    if (searchState.data && !!searchState.data.results) {
      searchCallbacks.onSuccess?.(searchState.data);
      setSearchCallbacks(null);
      return;
    }

    // Search returned data but without results
    if (searchState.data) {
      searchCallbacks.onError?.();
      setSearchCallbacks(null);
      return;
    }
  }, [searchState?.data, searchState?.error, searchState?.isLoading, searchCallbacks]);

  const clearCache = () => {
    dispatch(resetConcurrentSearch(searchKey));
  };

  return {
    isSearching: searchState?.isLoading || false,
    startSearch,
    clearCache,
  };
}

/**
 * Clears the underwriting cache for a specific AI prompt type.
 * Call this when a user manually retries an underwriting analysis to ensure
 * the new results are fetched everywhere (FundingTable, ShopInsuranceModal, etc.)
 *
 * @param dispatch - Redux dispatch function
 * @param aiPromptType - The AI prompt type that was retried (e.g., "underwriting-nw-individual")
 */
export function clearUnderwritingCacheForPromptType(
  dispatch: ReturnType<typeof useDispatch>,
  aiPromptType: string,
) {
  // AI prompt types follow pattern: underwriting-{carrier}-{individual|joint}
  // e.g., "underwriting-nw-individual", "underwriting-oa-joint"

  let searchKey: string | null = null;

  if (aiPromptType.includes('-nw-')) {
    searchKey = 'ai_underwriting_guidance_nw';
  } else if (aiPromptType.includes('-oa-')) {
    searchKey = 'ai_underwriting_guidance_oa';
  } else if (aiPromptType.includes('-cs-')) {
    searchKey = 'ai_underwriting_guidance_cs';
  } else if (aiPromptType.includes('-lincoln-')) {
    searchKey = 'ai_underwriting_guidance_lincoln';
  } else if (aiPromptType.includes('-securian-')) {
    searchKey = 'ai_underwriting_guidance_securian';
  }

  if (searchKey) {
    // console.log(
    //   `[clearUnderwritingCache] Clearing cache for aiPromptType: ${aiPromptType}, searchKey: ${searchKey}`,
    // );
    dispatch(resetConcurrentSearch(searchKey));
  } else {
    console.warn(
      `[clearUnderwritingCache] Unknown aiPromptType: ${aiPromptType}, cache not cleared`,
    );
  }

  // Also clear the generic underwriting guidance cache used by the modal
  dispatch(resetConcurrentSearch('ai_underwriting_guidance'));
}
